import { LoggerService } from "@Front/Services/LoggerService/LoggerService";
import { getUserFriendlyError } from "@Front/Utils/errorMessages";
import { releaseNetworkErrorToast, showErrorToasterAndNotificationCenter } from "@Front/Utils/errorToastDualChannel";
import type { ApiPayload, ProcessResponseOptions, QueryPayload } from "../types";
import { appendSerializedQueryParams } from "./queryParamHelpers";
import { coalesceThrownApiErrorText } from "@Front/Utils/userFacingMessageFromApiClientError";
import { isHttpStatusAuthSessionFailure } from "./httpAuthSessionFailurePolicy";
import { processResponse } from "./responseHelpers";
import { createFetchTimeoutAbort, type FetchWithTimeoutOptions } from "./fetchTimeoutHelpers";
import { isUserAbortError, rethrowNormalizedFetchFailure } from "@Front/Utils/networkTimeoutHelpers";

export type SendRequestOptions = Pick<ProcessResponseOptions, "ref" | "fileName" | "forceNoCacheRequest" | "skipSessionFailureOnAuthError">;

export type FetchWithNetworkLoggingOptions = FetchWithTimeoutOptions & {
	/**
	 * When false, skip `onError` (toaster / notification center). Use for silent bootstrap loads.
	 * Default true.
	 */
	invokeUserFacingErrorHandler?: boolean;
	/**
	 * Background/polling call (notifications, chat unread badge, banners, token refresh, SSE-driven refreshes):
	 * transport failures are logged only (never a network toast) and GET requests are retried silently with
	 * backoff. Thrown errors are marked so downstream `onError` calls (API factories) stay silent too.
	 */
	background?: boolean;
};

/** Silent retry backoff for background GET transport failures (filtering gateways cutting connections). */
const BACKGROUND_GET_RETRY_DELAYS_MS = [1000, 3000];

const BACKGROUND_REQUEST_ERROR_FLAG = "__lecoffreBackgroundRequestError";

/** Marks an error thrown by a background call so every downstream user-facing handler stays silent. */
export function markBackgroundRequestError(error: unknown): void {
	if (!error || typeof error !== "object") return;
	Object.defineProperty(error, BACKGROUND_REQUEST_ERROR_FLAG, { value: true, enumerable: false, configurable: true });
}

/** True when the failure comes from a background call: `onError` must log only, never toast. */
export function isBackgroundRequestError(error: unknown): boolean {
	return Boolean(error && typeof error === "object" && BACKGROUND_REQUEST_ERROR_FLAG in error);
}

/** External aborts (unmount/navigation) must not be retried; explicit timeouts ("TimeoutError") can be. */
function isExternalAbortError(error: unknown): boolean {
	return isUserAbortError(error);
}

function backgroundRetryDelaysFor(options: FetchWithNetworkLoggingOptions | undefined, method: string): readonly number[] {
	return options?.background && method === "GET" ? BACKGROUND_GET_RETRY_DELAYS_MS : [];
}

function waitMs(ms: number): Promise<void> {
	return new Promise((resolve) => setTimeout(resolve, ms));
}

function logTransportFailure(method: string, urlString: string, error: unknown): void {
	const errorMessage = coalesceThrownApiErrorText(error);
	const isConnectionReset = errorMessage.includes("ERR_CONNECTION_RESET") || errorMessage.includes("connection reset");
	const isNetworkErr = errorMessage.includes("Failed to fetch") || isConnectionReset || errorMessage.includes("network");
	LoggerService.error(`❌ [HttpClient] Network error on ${method}`, {
		url: urlString,
		error: errorMessage,
		errorType: error instanceof Error ? error.name : typeof error,
		isConnectionReset,
		isNetworkError: isNetworkErr,
	});
}

/** Terminal transport failure: mark background errors (silent) or invoke the user-facing handler. */
function finalizeTransportFailure(error: unknown, options?: FetchWithNetworkLoggingOptions): void {
	if (options?.background) {
		markBackgroundRequestError(error);
		return;
	}
	if (isUserAbortError(error)) {
		return;
	}
	if (options?.invokeUserFacingErrorHandler !== false) {
		onError(error);
	}
}

async function performFetchAttempt(
	urlString: string,
	init: RequestInit | undefined,
	options: FetchWithNetworkLoggingOptions | undefined,
	method: string,
): Promise<Response> {
	const { signal, cleanup } = createFetchTimeoutAbort(init, options, method, urlString);
	try {
		return await fetch(urlString, { ...init, signal });
	} catch (error) {
		rethrowNormalizedFetchFailure(signal, error);
	} finally {
		cleanup();
	}
}

/**
 * Low-level `fetch` with shared network-failure logging (and optional same UX as BaseApiService on transport errors).
 * Background calls (`options.background`) retry GET silently with backoff and never trigger the network toast.
 */
export async function fetchWithNetworkLogging(
	url: string | URL,
	init?: RequestInit,
	options?: FetchWithNetworkLoggingOptions,
): Promise<Response> {
	const urlString = typeof url === "string" ? url : url.href;
	const method = (init?.method ?? "GET").toUpperCase();
	const retryDelays = backgroundRetryDelaysFor(options, method);
	for (let attempt = 0; ; attempt++) {
		try {
			const response = await performFetchAttempt(urlString, init, options, method);
			releaseNetworkErrorToast();
			return response;
		} catch (error) {
			if (!isUserAbortError(error)) {
				logTransportFailure(method, urlString, error);
			}
			if (attempt < retryDelays.length && !isExternalAbortError(error)) {
				await waitMs(retryDelays[attempt] as number);
				continue;
			}
			finalizeTransportFailure(error, options);
			throw error;
		}
	}
}

interface HttpErrorPayload {
	http_status?: number;
	response?: {
		status?: number;
	};
	message?: string;
}

export function buildBody(body: ApiPayload): string {
	return JSON.stringify(body);
}

export function appendJsonSearchParams(url: URL, params: QueryPayload): void {
	appendSerializedQueryParams(url.searchParams, params);
}

type FriendlyError = { title: string; message: string };

function showErrorToastsIfNotSessionEnd(
	isSessionEnd: boolean,
	friendlyError: FriendlyError,
): void {
	if (isSessionEnd) return;
	showErrorToasterAndNotificationCenter(friendlyError.title, friendlyError.message);
}

export function onError(error: unknown): void {
	if (isBackgroundRequestError(error)) {
		LoggerService.warn("🔇 [HttpClient] Background request failure (toast suppressed)", {
			error: coalesceThrownApiErrorText(error),
		});
		return;
	}
	const err = error as HttpErrorPayload;
	const httpStatus = err?.http_status ?? err?.response?.status;
	const isSessionEnd = typeof httpStatus === "number" && isHttpStatusAuthSessionFailure(httpStatus);
	const friendlyError = getUserFriendlyError(err);
	showErrorToastsIfNotSessionEnd(isSessionEnd, friendlyError);
	LoggerService.error("🚫 API Error", {
		status: httpStatus,
		technicalMessage: err?.message,
		friendlyMessage: friendlyError.message,
	});
}

export async function sendRequest<T>(request: () => Promise<Response>, options?: SendRequestOptions): Promise<T> {
	let response: Response;
	try {
		response = await request();
	} catch (error) {
		const errorMessage = coalesceThrownApiErrorText(error);
		const isConnectionReset = errorMessage.includes("ERR_CONNECTION_RESET") || errorMessage.includes("connection reset");
		const isNetworkErr = errorMessage.includes("Failed to fetch") || isConnectionReset || errorMessage.includes("network");

		LoggerService.error("❌ [BaseApiService] sendRequest aborted due to network error", {
			error: errorMessage,
			errorType: error instanceof Error ? error.name : typeof error,
			isConnectionReset,
			isNetworkError: isNetworkErr,
		});
		throw error;
	}
	const { ref, fileName, forceNoCacheRequest, skipSessionFailureOnAuthError } = options ?? {};
	return processResponse<T>(response, request, { ref, fileName, forceNoCacheRequest, skipSessionFailureOnAuthError });
}
